import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isPmpAdminUser } from '../utils/firmAccess';

interface TdsPayment {
  id: number;
  tds_payment_number: string;
  total_tds_amount: number;
  payment_date: string;
  challan_number?: string;
  description?: string;
  notes?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
  // Laravel relationship - may be either camelCase or snake_case
  createdBy?: {
    id: number;
    name: string;
  };
  created_by_relation?: {
    id: number;
    name: string;
  };
  invoice_payments?: {
    id: number;
    tds_amount: number;
    vendor_invoice_payment: {
      id: number;
      vendor_invoice: {
        id: number;
        invoice_number: string;
        vendor_firm: {
          id: number;
          name: string;
        };
      };
    };
  }[];
}

interface TdsPaymentSummary {
  total_payments: number;
  total_tds: number;
  total_net: number;
  pending_filings: number;
}

interface PendingTdsPayment {
  id: number;
  amount: number;
  tds_amount: number;
  tds_rate: number;
  payment_date: string;
  payment_method: string;
  reference_number?: string;
  vendor_invoice: {
    id: number;
    invoice_number: string;
    vendor_firm: {
      id: number;
      name: string;
    };
    vendor_user: {
      id: number;
      name: string;
    };
  };
}

const TdsPayments: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { paymentId } = useParams<{ paymentId: string }>();
  const [payments, setPayments] = useState<TdsPayment[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<TdsPayment | null>(null);
  const [summary, setSummary] = useState<TdsPaymentSummary>({
    total_payments: 0,
    total_tds: 0,
    total_net: 0,
    pending_filings: 0
  });
  const [loading, setLoading] = useState(true);
  const [paymentDetailsLoading, setPaymentDetailsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    search: '',
    date_from: '',
    date_to: ''
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    challan_number: '',
    description: '',
    notes: ''
  });
  const [challanFile, setChallanFile] = useState<File | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [pendingTdsPayments, setPendingTdsPayments] = useState<PendingTdsPayment[]>([]);
  const [selectedInvoicePaymentIds, setSelectedInvoicePaymentIds] = useState<number[]>([]);
  const [pendingTdsLoading, setPendingTdsLoading] = useState(false);

  useEffect(() => {
    if (user && !isPmpAdminUser(user)) {
      navigate('/', { replace: true });
      return;
    }
    if (paymentId) {
      loadPaymentDetails();
    } else {
      loadPayments();
      loadSummary();
    }
  }, [paymentId, filters, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get(`/tds-payments?${params}`);
      if (response.data.success) {
        // Handle paginated response - access the data array within the pagination object
        const paymentsData = response.data.data?.data || response.data.data || [];
        // Debug logging can be enabled here if needed to troubleshoot creator field
        // if (paymentsData.length > 0) {
        //   console.log('First TDS payment object keys:', Object.keys(paymentsData[0]));
        // }
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      }
    } catch (err: any) {
      console.error('Error loading TDS payments:', err);
      setError(err.response?.data?.message || 'Failed to load TDS payments');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await api.get('/tds-payments/stats');
      if (response.data.success) {
        setSummary({
          total_payments: 0, // We'll calculate this from the payments array
          total_tds: response.data.data.yearly_tds || 0,
          total_net: 0, // We'll calculate this from the payments array
          pending_filings: response.data.data.pending_count || 0
        });
      }
    } catch (err: any) {
      console.error('Error loading TDS summary:', err);
    }
  };

  const loadPaymentDetails = async () => {
    if (!paymentId) return;
    
    try {
      setPaymentDetailsLoading(true);
      const response = await api.get(`/tds-payments/${paymentId}`);
      if (response.data.success) {
        setSelectedPayment(response.data.data);
      } else {
        setError('TDS Payment not found');
      }
    } catch (err: any) {
      console.error('Error loading TDS payment details:', err);
      setError(err.response?.data?.message || 'Failed to load TDS payment details');
    } finally {
      setPaymentDetailsLoading(false);
      setLoading(false);
    }
  };

  const loadPendingTdsPayments = async () => {
    try {
      setPendingTdsLoading(true);
      const response = await api.get('/tds-payments/pending');
      if (response.data.success) {
        const pendingData = response.data.data?.data || response.data.data || [];
        setPendingTdsPayments(Array.isArray(pendingData) ? pendingData : []);
      }
    } catch (err: any) {
      console.error('Error loading pending TDS payments:', err);
      setError(err.response?.data?.message || 'Failed to load pending TDS payments');
    } finally {
      setPendingTdsLoading(false);
    }
  };

  const handleCreatePayment = async () => {
    if (selectedInvoicePaymentIds.length === 0) {
      setError('Please select at least one pending invoice payment');
      return;
    }

    try {
      setCreateLoading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('payment_date', createForm.payment_date);
      formData.append('challan_number', createForm.challan_number);
      formData.append('description', createForm.description);
      formData.append('notes', createForm.notes);
      formData.append('invoice_payment_ids', JSON.stringify(selectedInvoicePaymentIds));
      
      // Add challan file if selected
      if (challanFile) {
        formData.append('challan_file', challanFile);
      }
      
      const response = await api.post('/tds-payments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setSuccess('TDS payment created successfully');
        setShowCreateDialog(false);
        setCreateForm({
          payment_date: new Date().toISOString().split('T')[0],
          challan_number: '',
          description: '',
          notes: ''
        });
        setChallanFile(null);
        setSelectedInvoicePaymentIds([]);
        loadPayments();
        loadSummary();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create TDS payment');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdatePayment = async (paymentId: number) => {
    const description = prompt('Please enter a description:');
    if (!description) return;

    try {
      const response = await api.put(`/tds-payments/${paymentId}`, {
        payment_date: new Date().toISOString().split('T')[0],
        description: description
      });
      if (response.data.success) {
        setSuccess('TDS payment updated successfully');
        loadPayments();
        loadSummary();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update TDS payment');
    }
  };

  const handleDownloadCertificate = async (paymentId: number) => {
    try {
      const response = await api.get(`/tds-payments/${paymentId}/certificate`, {
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'tds-certificate.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('TDS certificate downloaded successfully');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download certificate');
    }
  };


  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getCreatorName = (payment: TdsPayment): string => {
    // Check multiple possible field names for the creator
    return payment.createdBy?.name || 
           payment.created_by_relation?.name || 
           (payment as any).created_by?.name ||  // In case it's snake_case from backend
           'Unknown';
  };


  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  // If viewing a specific payment, show payment details
  if (paymentId && selectedPayment) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Button
              variant="text"
              onClick={() => navigate('/tds-payments')}
              sx={{ mb: 1 }}
            >
              ← Back to TDS Payments
            </Button>
            <Typography variant="h4">TDS Payment Details</Typography>
            <Typography variant="h6" color="text.secondary">
              {selectedPayment.tds_payment_number}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {paymentDetailsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Payment Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mb: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Payment Number</Typography>
                <Typography variant="body1">{selectedPayment.tds_payment_number}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                <Typography variant="body1">{formatDate(selectedPayment.payment_date)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Total TDS Amount</Typography>
                <Typography variant="body1">{formatCurrency(selectedPayment.total_tds_amount)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Challan Number</Typography>
                <Typography variant="body1">{selectedPayment.challan_number || 'N/A'}</Typography>
              </Box>
            </Box>
            
            {selectedPayment.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{selectedPayment.description}</Typography>
              </Box>
            )}
            
            {selectedPayment.notes && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Notes</Typography>
                <Typography variant="body1">{selectedPayment.notes}</Typography>
              </Box>
            )}

            {selectedPayment.invoice_payments && selectedPayment.invoice_payments.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>Related Invoice Payments</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice Number</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell align="right">TDS Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPayment.invoice_payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell>{payment.vendor_invoice_payment.vendor_invoice.invoice_number}</TableCell>
                          <TableCell>{payment.vendor_invoice_payment.vendor_invoice.vendor_firm.name}</TableCell>
                          <TableCell align="right">{formatCurrency(payment.tds_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    );
  }

  // If loading specific payment details but payment not found
  if (paymentId && paymentDetailsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Default list view
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">TDS Payments</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setShowCreateDialog(true);
            loadPendingTdsPayments();
          }}
        >
          Create TDS Payment
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Payments
            </Typography>
            <Typography variant="h4">
              {payments.length}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total TDS Amount
            </Typography>
            <Typography variant="h4">
              {formatCurrency(summary.total_tds)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Net Amount
            </Typography>
            <Typography variant="h4">
              {formatCurrency(summary.total_net)}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Pending Filings
            </Typography>
            <Typography variant="h4" color="error.main">
              {summary.pending_filings}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Filters</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField
            fullWidth
            label="Search"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            placeholder="Search by payment number, challan number, or description"
          />
          
          <TextField
            fullWidth
            label="From Date"
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          
          <TextField
            fullWidth
            label="To Date"
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* TDS Payments Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payment Number</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>TDS Amount</TableCell>
                <TableCell>Challan Number</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No TDS payments found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <Typography fontWeight="bold">
                        {payment.tds_payment_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(payment.payment_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight="bold" color="warning.main">
                        {formatCurrency(payment.total_tds_amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.challan_number || '-'}
                    </TableCell>
                    <TableCell>
                      {payment.description || '-'}
                    </TableCell>
                    <TableCell>
                      {getCreatorName(payment)}
                    </TableCell>
                    <TableCell>
                      {new Date(payment.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/tds-payments/${payment.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Certificate">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadCertificate(payment.id)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create TDS Payment Dialog */}
      <Dialog open={showCreateDialog} onClose={() => {
        setShowCreateDialog(false);
        setChallanFile(null);
        setCreateForm({
          payment_date: new Date().toISOString().split('T')[0],
          challan_number: '',
          description: '',
          notes: ''
        });
        setSelectedInvoicePaymentIds([]);
      }} maxWidth="lg" fullWidth>
        <DialogTitle>Create TDS Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Payment Details Form */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={createForm.payment_date}
                onChange={(e) => setCreateForm({ ...createForm, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />

              <TextField
                fullWidth
                label="Challan Number"
                value={createForm.challan_number}
                onChange={(e) => setCreateForm({ ...createForm, challan_number: e.target.value })}
                placeholder="e.g., BSR001234567890"
              />
            </Box>

            {/* Challan File Upload */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Challan File (Optional)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<AddIcon />}
                >
                  Upload Challan File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (10MB limit)
                        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
                        if (file.size > maxSize) {
                          setError('File size must be less than 10MB');
                          if (e.target) e.target.value = ''; // Clear the input
                          return;
                        }
                        setChallanFile(file);
                      }
                    }}
                  />
                </Button>
                {challanFile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {challanFile.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => setChallanFile(null)}
                      title="Remove file"
                    >
                      ×
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Accepted formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder="TDS payment description"
              />

              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                sx={{ gridColumn: '1 / -1' }}
                placeholder="Optional notes about this TDS payment"
              />
            </Box>

            {/* Pending TDS Payments Selection */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Pending Invoice Payments ({selectedInvoicePaymentIds.length} selected)
            </Typography>
            
            {pendingTdsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : pendingTdsPayments.length === 0 ? (
              <Alert severity="info">
                No pending TDS payments found. All invoice payment TDS amounts have been processed.
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedInvoicePaymentIds.length === pendingTdsPayments.length}
                        indeterminate={selectedInvoicePaymentIds.length > 0 && selectedInvoicePaymentIds.length < pendingTdsPayments.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedInvoicePaymentIds(pendingTdsPayments.map(p => p.id));
                          } else {
                            setSelectedInvoicePaymentIds([]);
                          }
                        }}
                      />
                    }
                    label="Select All"
                  />
                  {selectedInvoicePaymentIds.length > 0 && (
                    <Typography variant="body2" color="primary" sx={{ ml: 4 }}>
                      Total TDS Amount: {formatCurrency(
                        pendingTdsPayments
                          .filter(p => selectedInvoicePaymentIds.includes(p.id))
                          .reduce((sum, p) => sum + p.tds_amount, 0)
                      )}
                    </Typography>
                  )}
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">Select</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Payment Amount</TableCell>
                        <TableCell>TDS Rate</TableCell>
                        <TableCell>TDS Amount</TableCell>
                        <TableCell>Payment Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pendingTdsPayments.map((payment) => (
                        <TableRow 
                          key={payment.id}
                          selected={selectedInvoicePaymentIds.includes(payment.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedInvoicePaymentIds.includes(payment.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedInvoicePaymentIds([...selectedInvoicePaymentIds, payment.id]);
                                } else {
                                  setSelectedInvoicePaymentIds(selectedInvoicePaymentIds.filter(id => id !== payment.id));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {payment.vendor_invoice.invoice_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {payment.vendor_invoice.vendor_firm.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {payment.vendor_invoice.vendor_user.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>{payment.tds_rate}%</TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color="warning.main">
                              {formatCurrency(payment.tds_amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCreateDialog(false);
            setChallanFile(null);
            setCreateForm({
              payment_date: new Date().toISOString().split('T')[0],
              challan_number: '',
              description: '',
              notes: ''
            });
            setSelectedInvoicePaymentIds([]);
          }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreatePayment}
            variant="contained"
            disabled={selectedInvoicePaymentIds.length === 0 || createLoading}
            startIcon={createLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {createLoading ? 'Creating...' : `Create TDS Payment (${selectedInvoicePaymentIds.length} invoices)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TdsPayments;
